import { create } from 'zustand';
import { projectsAPI, accountsAPI, providersAPI, postsAPI, analyticsAPI } from '../api/client';

// En móvil el sidebar arranca cerrado
const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 768;

export const useAppStore = create((set, get) => ({
  // ─── Projects ───────────────────────────────────────
  projects: [],
  projectsLoading: false,
  fetchProjects: async () => {
    set({ projectsLoading: true });
    try {
      const { data } = await projectsAPI.list();
      set({ projects: data, projectsLoading: false });
    } catch {
      set({ projects: [], projectsLoading: false });
    }
  },
  addProject: async (p) => {
    try {
      const { data } = await projectsAPI.create(p);
      set(s => ({ projects: [data, ...s.projects] }));
    } catch {}
  },
  updateProject: async (id, payload) => {
    try {
      const { data } = await projectsAPI.update(id, payload);
      set(s => ({ projects: s.projects.map(p => p.id === id ? data : p) }));
    } catch {}
  },
  deleteProject: async (id) => {
    try {
      await projectsAPI.delete(id);
      set(s => ({ projects: s.projects.filter(p => p.id !== id) }));
    } catch {}
  },

  // ─── Social Accounts ────────────────────────────────
  accounts: [],
  accountsLoading: false,
  fetchAccounts: async () => {
    set({ accountsLoading: true });
    try {
      const { data } = await accountsAPI.list();
      set({ accounts: data, accountsLoading: false });
    } catch {
      set({ accounts: [], accountsLoading: false });
    }
  },
  updateAccount: (id, payload) => set(s => ({ accounts: s.accounts.map(a => a.id === id ? { ...a, ...payload } : a) })),
  removeAccount: async (id) => {
    try {
      await accountsAPI.disconnect(id);
      set(s => ({ accounts: s.accounts.filter(a => a.id !== id) }));
    } catch {}
  },

  // ─── AI Providers ───────────────────────────────────
  providers: [],
  providersLoading: false,
  fetchProviders: async () => {
    set({ providersLoading: true });
    try {
      const { data } = await providersAPI.list();
      set({ providers: data, providersLoading: false });
    } catch {
      set({ providers: [], providersLoading: false });
    }
  },
  setActiveProvider: async (id) => {
    try {
      await providersAPI.setActive(id);
      set(s => ({ providers: s.providers.map(p => ({ ...p, is_active: p.id === id })) }));
    } catch {}
  },
  addProvider: async (p) => {
    try {
      const { data } = await providersAPI.create(p);
      set(s => ({ providers: [...s.providers, data] }));
    } catch {}
  },
  updateProvider: async (id, payload) => {
    try {
      const { data } = await providersAPI.update(id, payload);
      set(s => ({ providers: s.providers.map(p => p.id === id ? data : p) }));
    } catch {}
  },
  deleteProvider: async (id) => {
    try {
      await providersAPI.delete(id);
      set(s => ({ providers: s.providers.filter(p => p.id !== id) }));
    } catch {}
  },

  // ─── Posts ──────────────────────────────────────────
  posts: [],
  postsLoading: false,
  fetchPosts: async () => {
    set({ postsLoading: true });
    try {
      const { data } = await postsAPI.list();
      set({ posts: Array.isArray(data) ? data : data.items || [], postsLoading: false });
    } catch {
      set({ posts: [], postsLoading: false });
    }
  },
  addPost: (post) => set(s => ({ posts: [post, ...s.posts] })),
  updatePost: (id, payload) => set(s => ({ posts: s.posts.map(p => p.id === id ? { ...p, ...payload } : p) })),
  removePost: async (id) => {
    try {
      await postsAPI.delete(id);
      set(s => ({ posts: s.posts.filter(p => p.id !== id) }));
    } catch {}
  },

  // ─── Analytics ──────────────────────────────────────
  analytics: null,
  analyticsLoading: false,
  fetchAnalytics: async () => {
    set({ analyticsLoading: true });
    try {
      const { data } = await analyticsAPI.dashboard();
      set({ analytics: data, analyticsLoading: false });
    } catch {
      set({ analytics: null, analyticsLoading: false });
    }
  },

  // ─── UI State ───────────────────────────────────────
  sidebarOpen: !isMobile(),
  toggleSidebar: () => set(s => ({ sidebarOpen: !s.sidebarOpen })),
  notifications: [],
  addNotification: (n) => set(s => ({ notifications: [{ ...n, id: Date.now(), read: false }, ...s.notifications] })),
  markNotificationRead: (id) => set(s => ({
    notifications: s.notifications.map(n => n.id === id ? { ...n, read: true } : n)
  })),
  markAllRead: () => set(s => ({ notifications: s.notifications.map(n => ({ ...n, read: true })) })),
}));
